<template>
    <v-form @submit.prevent="upsertTimeOff" ref="form">
        <v-card dense class="rounded-4">

            <!-- HEADER -->
            <v-toolbar flat height="45px">

                <span class="font-weight-bold">
                    {{ timeOff.mode == 'Add'
                        ? 'Add Personal Time Off'
                        : 'Edit Personal Time Off'
                    }}
                </span>

                <v-spacer></v-spacer>

                <help-link
                    appId="attendance"
                    :pageId="enableDateRange ? 'timeoff-page' : 'timeoff-daily'"
                    color="black"
                />

                <v-btn icon @click.native="closeDialog">
                    <v-icon>close</v-icon>
                </v-btn>

            </v-toolbar>

            <!-- ERROR -->
            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                Error encountered.
                {{ errorMessage }}
            </v-alert>

            <!-- APPROVED -->
            <v-alert v-if="isApproved" :value="true" color="warning" icon="warning" outline class="mt-8">
                This is already approved.
                Clicking Save will update back to For Review.
            </v-alert>

            <!-- INVALID HOURS -->
            <v-alert v-if="!validInputHours" :value="true" color="error" icon="error" outline class="mt-8 mr-10 ml-10">
                Requested hours exceeds available balance.
            </v-alert>

            <v-card-text dense>
                <v-layout row wrap>
                    <v-flex xs12 class="form-content">

                        <!-- DATE -->
                        <v-layout row wrap>

                            <!-- SINGLE DATE -->
                            <template v-if="!enableDateRange">
                                <v-flex xs12>
                                    <v-text-field label="Date" v-model="transDate" disabled/>
                                </v-flex>
                            </template>

                            <!-- DATE RANGE -->
                            <template v-else>
                                <v-flex xs12 sm6 :class="isMobile?'':'pr-15'">
                                    <v-menu
                                        v-model="menuTransDate"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="290px"
                                    >

                                        <template v-slot:activator="{ on }">
                                            <v-text-field
                                                label="Start Date"
                                                v-model="transDate"
                                                readonly
                                                v-on="on"
                                                :rules="[rules.required]"
                                                :disabled="disabled"
                                            />
                                        </template>

                                        <v-date-picker v-model="transDate" :allowed-dates="allowedDates"/>

                                    </v-menu>
                                </v-flex>

                                <v-flex xs12 sm6 :class="isMobile?'':'pl-15'">
                                    <v-menu
                                        v-model="menuEndDate"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="290px"
                                    >
                                        <template v-slot:activator="{ on }">

                                            <v-text-field
                                                label="End Date"
                                                v-model="endDate"
                                                readonly
                                                v-on="on"
                                                :rules="[rules.required]"
                                                :disabled="disabled"
                                            />

                                        </template>

                                        <v-date-picker v-model="endDate" :allowed-dates="allowedDatesForEndDate" />

                                    </v-menu>
                                </v-flex>
                            </template>
                        </v-layout>

                        <!-- TYPE -->
                        <v-layout row>
                            <v-flex xs12>
                                <v-autocomplete
                                    label="Type"
                                    v-model="timeOff.leaveTypeId"
                                    :disabled="disabled || !leavesBalancesLoaded"
                                    :items="leavesAllocation"
                                    :rules="[rules.required]"
                                    chips
                                    item-text="leaveName"
                                    item-value="leaveTypeId"
                                    @change="validateHours"
                                >

                                    <template v-slot:selection="data">
                                        <v-chip
                                            :selected="data.selected"
                                            class="chip--select-multi ml-0"
                                        >
                                            {{ data.item.leaveName }}
                                        </v-chip>
                                    </template>
                                </v-autocomplete>
                            </v-flex>
                        </v-layout>

                        <!-- HOURS -->
                        <v-layout row>
                            <v-flex xs12>
                                <v-text-field
                                    :label="enableDateRange ? 'Hours per day' : 'Hours'"
                                    v-model="timeOff.hours"
                                    :rules="[rules.required, rules.valid_hours]"
                                    type="number"
                                    min="0"
                                    step="0.25"
                                    hint="In decimal format. (1.5 = 1hour 30mins)"
                                    persistent-hint
                                />
                            </v-flex>
                        </v-layout>

                        <!-- INCLUDE WEEKENDS -->
                        <v-layout row wrap v-if="enableDateRange">
                            <v-flex xs12 sm6>
                                <v-switch
                                    v-model="includeWeekends"
                                    label="Include weekends"
                                    color="primary"
                                />
                            </v-flex>
                            <v-flex xs12 sm6>
                                <div :class="isMobile?'':'mt-21'"><b>Total Hours: {{ totalRequestedHours }}</b></div>
                                <div></div>
                            </v-flex>
                        </v-layout>

                        <!-- REMARKS -->
                        <v-layout row class="mt-10">
                            <v-flex xs12>
                                <v-textarea
                                    v-model="timeOff.remarks"
                                    :rules="[rules.required]"
                                    maxlength="150"
                                    hint="150 characters max"
                                    label="Remarks"
                                    rows="1"
                                />

                            </v-flex>
                        </v-layout>
                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="mr-25">
                <v-spacer></v-spacer>
                <v-btn v-if="timeOff.mode == 'Add'" @click="clearForm" :disabled="disabled">
                    Clear
                </v-btn>
                <v-btn color="primary" type="submit" :loading="loading" :disabled="disabled || !validInputHours">
                    {{ timeOff.mode == 'Edit' ? 'Update' : 'Save' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>

<script>

import timeOffsApi from '@/api/timeoffs'
import leavesAllocationApi from '@/api/leavesallocation'
import commonfn from '@/common/functions'   //for refactor

export default {
    props: {
        enableDateRange: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            commonfn,
            timeOff: {},
            transDate: null,
            endDate: null,
            loading: false,
            disabled: false,
            error: false,
            errorMessage: '',
            validInputHours: true,
            totalRequestedHours: 0,
            includeWeekends: false,
            leavesBalancesLoaded: false,
            leavesAllocation: [],
            cutoff: null,
            menuTransDate: false,
            menuEndDate: false,
            rules: {
                required: value =>
                    !!value || 'Required.',
                valid_hours: value =>
                    value > 0 || 'Should be greater than zero'
            }
        }
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink')
    },    
    methods: {
        async setParams(data) {

            this.clearForm()
            this.timeOff = data
            this.cutoff = data.cutoff || null

            if (data.mode == 'Edit') {

                this.transDate = this.$moment(data.transDate).format('YYYY-MM-DD')
                this.endDate = data.endDate
                        ? this.$moment(data.endDate)
                            .format('YYYY-MM-DD')
                        : this.transDate
                this.timeOff.leaveTypeId = data.type
                this.timeOff.hours = data.hours
                this.timeOff.remarks = data.remarks
                this.timeOff.timeOffId = data.timeOffId

            } else {
                this.transDate = this.$moment(data.transDate).format('YYYY-MM-DD')
                this.endDate = this.transDate
            }

            await this.getAvailableLeaves()

            this.updateTotalHours()
        },
        allowedDates(val) {

            if (!this.cutoff)
                return true

            return (
                this.$moment(this.cutoff.cutoffStart)
                    .format('YYYY-MM-DD')
                <=
                this.$moment(val)
                    .format('YYYY-MM-DD')
                &&
                this.$moment(this.cutoff.cutoffEnd)
                    .format('YYYY-MM-DD')
                >=
                this.$moment(val)
                    .format('YYYY-MM-DD')
            )
        },
        allowedDatesForEndDate(val) {
            return (
                this.$moment(val)
                    .isSameOrAfter(this.transDate, 'day')
                &&
                this.allowedDates(val)
            )
        },
        onTransDateChange() {
            this.menuTransDate = false
            this.endDate = this.transDate
            this.getAvailableLeaves()
            this.updateTotalHours()
        },
        onEndDateSelected() {
            this.menuEndDate = false
            this.updateTotalHours()
        },
        async getAvailableLeaves() {

            this.leavesBalancesLoaded = false
            this.disabled = true

            try {

                this.leavesAllocation = []
                const response = await leavesAllocationApi.getAvailable(this.transDate)

                if (response && response.status == 200) {

                    response.data.items.forEach(item => {

                        var balanceHours = item.earnedHours > item.balanceHours
                                        ? item.balanceHours
                                        : item.earnedHours

                        this.leavesAllocation.push({
                            leaveTypeId: item.leaveTypeId,
                            balanceHours: balanceHours,
                            leaveName: item.earnedHours == -1
                                    ? item.leaveName
                                    : `${item.leaveName} (${balanceHours} hrs available)`
                        })
                    })

                    this.leavesBalancesLoaded = true
                }

            } catch (error) {
                this.error = true
                this.errorMessage = error
            } finally {
                this.disabled = false
            }
        },
        formatHours() {
            if (!this.timeOff.hours)
                return

            if (parseFloat(this.timeOff.hours) > 8)
                this.timeOff.hours = 8

            this.timeOff.hours = parseFloat(this.timeOff.hours).toFixed(2)

            this.updateTotalHours()
        },
        updateTotalHours() {
            this.validInputHours = true

            if (!this.timeOff.hours)
                return

            var dailyHours =
                parseFloat(this.timeOff.hours)

            if (!this.enableDateRange) {
                this.totalRequestedHours = dailyHours
            } else {
                if (!this.transDate || !this.endDate)
                    return

                let total = 0
                let current = this.$moment(this.transDate)
                const end = this.$moment(this.endDate)

                while (current.isSameOrBefore(end, 'day')) {

                    const day = current.day()

                    if (this.includeWeekends || (day !== 0 && day !== 6)) {
                        total += dailyHours
                    }

                    current.add(1, 'day')
                }

                this.totalRequestedHours = total
            }

            this.totalRequestedHours = parseFloat(this.totalRequestedHours).toFixed(2)

            this.validateHours()
        },
        validateHours() {
            this.validInputHours = true

            const selected = this.leavesAllocation.find(x => {
                return x.leaveTypeId == this.timeOff.leaveTypeId
            })

            if (!selected)
                return

            if (selected.balanceHours == -1)
                return

            if (parseFloat(this.totalRequestedHours) > parseFloat(selected.balanceHours)) {
                this.validInputHours = false
            }
        },
        async upsertTimeOff() {
            if (!this.$refs.form.validate() || !this.validInputHours) {
                return
            }

            try {
                this.loading = true
                this.disabled = true

                const payload = {
                    timeOffId: this.timeOff.timeOffId || 0,
                    transDate: this.transDate,
                    endDate: this.enableDateRange
                            ? this.endDate
                            : this.transDate,
                    hours: this.timeOff.hours,
                    type: this.timeOff.leaveTypeId,
                    includeWeekends: this.includeWeekends,
                    remarks: this.timeOff.remarks
                }

                let response = null

                if (this.timeOff.mode == 'Edit')
                    response = await timeOffsApi.update(payload)
                else
                    response = await timeOffsApi.add(payload)

                if (response && response.status == 200) {
                    this.closeDialog()
                    this.$emit('send', null)
                }

            } catch (error) {
                this.error = true
                this.errorMessage = error
            } finally {
                this.loading = false
                this.disabled = false
            }
        },
        clearForm() {
            this.error = false
            this.errorMessage = ''

            this.loading = false
            this.disabled = false

            this.validInputHours = true
            this.totalRequestedHours = 0
            this.leavesBalancesLoaded = false
            this.includeWeekends = false
            if (this.$refs.form)
                this.$refs.form.reset()
        },
        closeDialog() {
            this.$emit('update:dialogPTO', false)
        }
    },
    watch: {
        transDate() {
            this.onTransDateChange()
        },
        endDate() {
            this.menuEndDate = false
            this.updateTotalHours()
        },
        includeWeekends() {
            this.updateTotalHours()
        },
        'timeOff.hours'() {
            this.formatHours()
        }
    },
    computed: {
        isApproved() {
            return this.timeOff
                && this.timeOff.status ==
                commonfn.getStatusCode('Approved')
        }
    },
}
</script>

<style scoped>

.form-content {
    padding:10px 25px 0px 25px;
}

</style>